/**
 * validatePrivacyHref — the ONE check on a marketing site's cookie-banner privacy destination.
 *
 * `<CookieConsentProvider>` declares `privacyHref` `isRequired`, but React 19 no longer runs
 * `propTypes`, so that declaration checks nothing at runtime. That is how every marketing site
 * shipped a banner whose privacy link had no `href` at all (TOOLING-AND-DOCS-GEMS, owner ruling
 * "fix the cookie-banner privacy link now", 2026-09-30). The two places that mount the marketing
 * provider stack, `createMarketingApp` (the live site) and `createA11yPageHarness` (the axe tests
 * that claim to mirror it), therefore both refuse a missing or foreign destination at their own
 * boundary, through this one function, so the rule cannot drift between them.
 *
 * It must be a path on THIS site (`/…`): the banner is shown on the marketing host, whose privacy
 * page is one of its own public routes. A protocol-relative `//…` would leave the site, so it is
 * refused too. That the path is one the portal's router actually declares is proved statically by
 * `tests/cross-app/contracts/marketingCookieBannerPrivacyHref.contract.test.js`, against the same
 * route resolver the portal route-registry contract uses.
 *
 * @module @puffin-core/marketing-shell/factories/validatePrivacyHref
 */

/**
 * Refuse anything but a site-relative path.
 *
 * @param {*} privacyHref - The value the caller was handed.
 * @param {string} subject - Who received it and under which name, e.g.
 *   `'createMarketingApp: config.cookieConsent.privacyHref'`. It leads the error message, so a wrong
 *   wiring is diagnosable from the message alone.
 * @returns {void}
 * @throws {TypeError} when `privacyHref` is not a string starting with a single `/`.
 */
export default function validatePrivacyHref(privacyHref, subject) {
  if (
    typeof privacyHref !== 'string' ||
    !privacyHref.startsWith('/') ||
    privacyHref.startsWith('//')
  ) {
    throw new TypeError(
      `${subject} (a site-relative path such as '/privacy') is required — the cookie banner ` +
        "links the site's privacy policy, and there is no default. Pass the portal route " +
        `registry's PRIVACY constant (got ${JSON.stringify(privacyHref)}).`,
    );
  }
}
